/* Full-screen reading/editing mode for lesson notes, shared by the three places that
   show a note: the live lesson page, the offline downloaded-lesson page and the note
   editor. A plain class instantiated per component rather than an injectable service —
   the state is per-view, so a root singleton would be wrong, and nothing here needs DI.

   A CSS overlay rather than the Fullscreen API: iOS Safari does not implement
   requestFullscreen on elements, and phones are most of the audience for this.

   "Chrome" here means the floating controls drawn over the note — the exit button and
   the page navigation. They reveal on pointer movement and fade out again once the
   reader has been still, so they are reachable without sitting permanently on top of
   the text. On touch, where there is no hover, a tap on the note toggles them. */

const IDLE_MS = 2500;

export class FullscreenNoteChrome {
  active = false;
  chromeVisible = true;

  private idleTimer: any = null;
  /* Set while the pointer is over the controls themselves. Without it the bar fades
     out from under a cursor that is resting on the Next button. */
  private held = false;

  get chromeHidden(): boolean {
    return this.active && !this.chromeVisible;
  }

  toggle(): void {
    this.set(!this.active);
  }

  set(on: boolean): void {
    this.active = on;
    // The overlay is position:fixed, so the page behind it still scrolls under the
    // reader's finger unless the body is pinned. The class is on <body>, which outlives
    // the component, so destroy() must clear it.
    document.body.classList.toggle('lesson-note-fullscreen-open', on);
    if (on) {
      this.poke();
    } else {
      this.clearTimer();
      this.chromeVisible = true;
    }
  }

  /** Pointer moved over the note — show the controls and restart the idle countdown. */
  poke(): void {
    if (!this.active) return;
    this.chromeVisible = true;
    this.clearTimer();
    if (!this.held) {
      this.idleTimer = setTimeout(() => {
        this.chromeVisible = false;
        this.idleTimer = null;
      }, IDLE_MS);
    }
  }

  /** Touch has no hover, so a tap on the note body flips the controls instead. */
  tapped(): void {
    if (!this.active) return;
    if (this.chromeVisible) {
      this.clearTimer();
      this.chromeVisible = false;
    } else {
      this.poke();
    }
  }

  hold(): void {
    this.held = true;
    this.chromeVisible = true;
    this.clearTimer();
  }

  release(): void {
    this.held = false;
    this.poke();
  }

  destroy(): void {
    this.clearTimer();
    document.body.classList.remove('lesson-note-fullscreen-open');
  }

  private clearTimer(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }
}
